import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingsApi, messagesApi, reviewsApi, disputesApi, getPhotoUrl } from '../utils/api';
import { paymentsApi } from '../utils/payments';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { BOOKING_STATUS } from '../utils/constants';
import { toast } from 'sonner';
import { Star, Chat, CreditCard, XCircle, Warning, Flag } from '@phosphor-icons/react';

// Category-specific images for bookings (fallback when no photo)
const CATEGORY_BOOKING_IMAGES = {
  'reading-room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'reading_room': 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'workspace': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
  'home-gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home_gym': 'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=80',
  'home-theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'home_theater': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
  'game-room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'game_room': 'https://images.unsplash.com/photo-1511882150382-421056c89033?w=800&q=80',
  'backyard': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'pool': 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=800&q=80',
  'dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'private_dining': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'hobby-room': 'https://images.unsplash.com/photo-1452860606245-08befc0ff44b?w=800&q=80',
  'wellness': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
  'creator-studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'creative_studio': 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80',
  'outdoor-sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'outdoor_sports': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
};

const getBookingImage = (booking) => {
  // Try to use listing photo first
  if (booking.listing_photo) {
    const photoUrl = getPhotoUrl(booking.listing_photo);
    if (photoUrl && !photoUrl.includes('undefined')) {
      return photoUrl;
    }
  }
  // Fall back to category-specific image
  return CATEGORY_BOOKING_IMAGES[booking.category] || CATEGORY_BOOKING_IMAGES['default'];
};

export default function GuestBookings() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // Cancellation state
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancellationPreview, setCancellationPreview] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Dispute state
  const [showDisputeModal, setShowDisputeModal] = useState(null);
  const [disputeData, setDisputeData] = useState({ reason: '', description: '' });
  const [disputeEligibility, setDisputeEligibility] = useState({});
  const [submittingDispute, setSubmittingDispute] = useState(false);

  useEffect(() => {
    loadBookings();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      setPaymentProcessing(true);
      const response = await paymentsApi.getPaymentStatus(sessionId, token);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your booking is confirmed.');
        loadBookings();
      } else {
        toast.info('Checking payment status...');
        // Poll a few more times
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retry = await paymentsApi.getPaymentStatus(sessionId, token);
        if (retry.data.payment_status === 'paid') {
          toast.success('Payment successful! Your booking is confirmed.');
          loadBookings();
        }
      }
    } catch (error) {
      toast.error('Payment verification failed');
    } finally {
      setPaymentProcessing(false);
      // Clean URL
      window.history.replaceState({}, '', '/guest/bookings');
    }
  };

  const handlePayNow = async (bookingId) => {
    try {
      setPaymentProcessing(true);
      const response = await paymentsApi.createCheckout(bookingId, token);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await bookingsApi.getAll(token);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const loadMessages = async (bookingId) => {
    try {
      const response = await messagesApi.getByBooking(bookingId, token);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (bookingId) => {
    if (!newMessage.trim()) return;
    
    try {
      await messagesApi.create({ booking_id: bookingId, content: newMessage }, token);
      setNewMessage('');
      loadMessages(bookingId);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const openCancelModal = async (booking) => {
    setShowCancelModal(booking);
    setCancelReason('');
    try {
      const res = await bookingsApi.getCancellationPreview(booking.id, token);
      setCancellationPreview(res.data);
    } catch (error) {
      console.error('Failed to get cancellation preview:', error);
      setCancellationPreview(null);
    }
  };

  const handleCancelBooking = async () => {
    if (!showCancelModal) return;
    
    setCancelling(true);
    try {
      const res = await bookingsApi.cancel(showCancelModal.id, cancelReason, token);
      toast.success(res.data.message);
      setShowCancelModal(null);
      setCancellationPreview(null);
      setCancelReason('');
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitReview = async (bookingId) => {
    try {
      await reviewsApi.create({
        booking_id: bookingId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        review_type: 'guest_to_host',
      }, token);
      toast.success('Review submitted!');
      setShowReviewForm(null);
      setReviewData({ rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const openDisputeModal = async (booking) => {
    setShowDisputeModal(booking);
    setDisputeData({ reason: '', description: '' });
    
    // Check dispute eligibility
    try {
      const res = await disputesApi.checkEligibility(booking.id, token);
      setDisputeEligibility(prev => ({ ...prev, [booking.id]: res.data }));
    } catch (error) {
      console.error('Failed to check dispute eligibility:', error);
      setDisputeEligibility(prev => ({ 
        ...prev, 
        [booking.id]: { can_dispute: false, reason: 'Failed to check eligibility' } 
      }));
    }
  };

  const handleSubmitDispute = async () => {
    if (!showDisputeModal || !disputeData.reason || !disputeData.description) return;
    
    setSubmittingDispute(true);
    try {
      const res = await disputesApi.create({
        booking_id: showDisputeModal.id,
        reason: disputeData.reason,
        description: disputeData.description
      }, token);
      
      toast.success(res.data.message || 'Dispute submitted successfully');
      setShowDisputeModal(null);
      setDisputeData({ reason: '', description: '' });
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const openMessaging = (booking) => {
    setSelectedBooking(booking);
    loadMessages(booking.id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case BOOKING_STATUS.APPROVED: return 'bg-green-600/20 text-green-400 border-green-600/30';
      case BOOKING_STATUS.PENDING: return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30';
      case BOOKING_STATUS.REJECTED: return 'bg-red-600/20 text-red-400 border-red-600/30';
      case BOOKING_STATUS.CANCELLED: return 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30';
      case BOOKING_STATUS.CONFIRMED: return 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30';
      case BOOKING_STATUS.COMPLETED: return 'bg-blue-600/20 text-blue-400 border-blue-600/30';
      default: return 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl font-serif text-white mb-8">My Bookings</h1>

        {bookings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-zinc-400 mb-6">You haven't made any bookings yet.</p>
            <Button
              onClick={() => navigate('/search')}
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
            >
              Explore Spaces
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {bookings.map((booking) => (
              <div key={booking.id} data-testid={`booking-${booking.id}`} className="bg-[#121212] border border-white/10 p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-full md:w-48 h-32 flex-shrink-0">
                    <img
                      src={getBookingImage(booking)}
                      alt={booking.listing_title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = CATEGORY_BOOKING_IMAGES[booking.category] || CATEGORY_BOOKING_IMAGES['default'];
                      }}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-serif text-white mb-1">{booking.listing_title}</h3>
                        <p className="text-zinc-500 text-sm">Host: {booking.host_name}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 border ${getStatusColor(booking.status)}`}>
                        {booking.status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-zinc-400 mb-4">
                      <div>
                        <p className="text-zinc-600 mb-1">Date</p>
                        <p className="text-white">{booking.booking_date}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 mb-1">Time</p>
                        <p className="text-white">{booking.start_time} - {booking.end_time}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 mb-1">Guests</p>
                        <p className="text-white">{booking.guests_count}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 mb-1">Price Breakdown</p>
                        {booking.listing_price && booking.guest_service_fee ? (
                          <div className="space-y-1">
                            <p className="text-zinc-400 text-xs">Space: ${booking.listing_price?.toFixed(2)}</p>
                            <p className="text-zinc-400 text-xs">Service fee: ${booking.guest_service_fee?.toFixed(2)}</p>
                            <p className="text-white font-semibold">Total: ${booking.total_price}</p>
                          </div>
                        ) : (
                          <p className="text-white font-semibold">${booking.total_price}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => navigate(`/listing/${booking.listing_id}`)}
                        className="bg-transparent border border-white/20 text-white hover:bg-white/5"
                      >
                        View Listing
                      </Button>
                      
                      {booking.status === BOOKING_STATUS.APPROVED && (
                        <>
                          <Button
                            data-testid={`pay-now-${booking.id}`}
                            onClick={() => handlePayNow(booking.id)}
                            disabled={paymentProcessing}
                            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                          >
                            <CreditCard size={20} className="mr-2" weight="duotone" />
                            {paymentProcessing ? 'Processing...' : 'Pay Now'}
                          </Button>
                          <Button
                            data-testid={`message-host-${booking.id}`}
                            onClick={() => openMessaging(booking)}
                            className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                          >
                            <Chat size={20} className="mr-2" weight="duotone" />
                            Message Host
                          </Button>
                        </>
                      )}
                      
                      {(booking.status === BOOKING_STATUS.PENDING || booking.status === BOOKING_STATUS.CONFIRMED) && (
                        <Button
                          data-testid={`cancel-booking-${booking.id}`}
                          onClick={() => openCancelModal(booking)}
                          className="bg-red-600/20 border border-red-600/30 text-red-400 hover:bg-red-600 hover:text-white"
                        >
                          <XCircle size={18} className="mr-2" />
                          Cancel Booking
                        </Button>
                      )}
                      
                      {booking.status === BOOKING_STATUS.COMPLETED && (
                        <>
                          <Button
                            data-testid={`review-button-${booking.id}`}
                            onClick={() => setShowReviewForm(booking.id)}
                            className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                          >
                            <Star size={20} className="mr-2" weight="duotone" />
                            Leave Review
                          </Button>
                          
                          {/* Dispute Button - only if eligible */}
                          {!booking.has_dispute && (
                            <Button
                              data-testid={`raise-dispute-${booking.id}`}
                              onClick={() => openDisputeModal(booking)}
                              className="bg-transparent border border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                            >
                              <Flag size={18} className="mr-2" weight="duotone" />
                              Raise Dispute
                            </Button>
                          )}
                          
                          {booking.has_dispute && (
                            <span className="px-3 py-2 text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
                              Dispute Filed
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Review Form */}
                    {showReviewForm === booking.id && (
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <h4 className="text-white font-semibold mb-4">Leave a Review</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="text-zinc-400 mb-2 block">Rating</label>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewData({ ...reviewData, rating: star })}
                                  className="text-2xl"
                                >
                                  <Star
                                    size={32}
                                    weight={star <= reviewData.rating ? 'fill' : 'regular'}
                                    className={star <= reviewData.rating ? 'text-[#D4AF37]' : 'text-zinc-600'}
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-zinc-400 mb-2 block">Comment</label>
                            <Textarea
                              data-testid="review-comment-input"
                              value={reviewData.comment}
                              onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                              placeholder="Share your experience..."
                              className="bg-transparent border-white/20 text-white"
                              rows={3}
                            />
                          </div>
                          <div className="flex gap-3">
                            <Button
                              data-testid="submit-review-button"
                              onClick={() => handleSubmitReview(booking.id)}
                              className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                            >
                              Submit Review
                            </Button>
                            <Button
                              onClick={() => setShowReviewForm(null)}
                              className="bg-transparent border border-white/20 text-white hover:bg-white/5"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Messaging Modal */}
        {selectedBooking && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
            <div className="bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-serif text-white">{selectedBooking.listing_title}</h3>
                  <p className="text-sm text-zinc-500">Chat with {selectedBooking.host_name}</p>
                </div>
                <button
                  data-testid="close-messaging"
                  onClick={() => setSelectedBooking(null)}
                  className="text-zinc-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 ? (
                  <p className="text-zinc-500 text-center">No messages yet. Start the conversation!</p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="flex flex-col">
                      <p className="text-xs text-zinc-600 mb-1">{msg.sender_name}</p>
                      <div className="bg-[#0A0A0A] border border-white/10 p-3">
                        <p className="text-zinc-300">{msg.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="p-6 border-t border-white/10">
                <div className="flex gap-3">
                  <Input
                    data-testid="message-input"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="bg-transparent border-white/20 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(selectedBooking.id)}
                  />
                  <Button
                    data-testid="send-message-button"
                    onClick={() => handleSendMessage(selectedBooking.id)}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">Cancel Booking</h3>
                <p className="text-zinc-500 text-sm mt-1">{showCancelModal.listing_title}</p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Cancellation Preview */}
                {cancellationPreview && (
                  <div className="bg-[#0a0a0a] border border-white/10 p-4 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Warning size={18} className="text-yellow-400" />
                      <span className="text-zinc-400">{cancellationPreview.tier_description}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-500">Your Refund</p>
                        <p className="text-emerald-400 font-semibold text-lg">
                          ${cancellationPreview.if_guest_cancels.guest_refund.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Original Payment</p>
                        <p className="text-white font-semibold text-lg">
                          ${cancellationPreview.original_booking.total_paid?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    {cancellationPreview.if_guest_cancels.guest_refund === 0 && (
                      <p className="text-red-400 text-sm">
                        ⚠️ No refund - cancellation is within 24 hours of booking start time.
                      </p>
                    )}
                    
                    {cancellationPreview.if_guest_cancels.guest_refund > 0 && (
                      <p className="text-zinc-500 text-xs">
                        Service fee is non-refundable. Refunds are processed within 7-14 business days.
                      </p>
                    )}
                  </div>
                )}

                {/* Reason Input */}
                <div>
                  <label className="text-zinc-400 mb-2 block text-sm">Reason for cancellation (optional)</label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Let us know why you're cancelling..."
                    className="bg-[#0a0a0a] border-white/10 text-white"
                    rows={3}
                  />
                </div>

                {/* Policy Summary */}
                <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded text-xs text-yellow-200/80">
                  <p className="font-semibold mb-1">Cancellation Policy:</p>
                  <ul className="space-y-1">
                    <li>• &gt;48 hrs before booking start: 100% refund of host amount</li>
                    <li>• 24-48 hrs before booking start: 50% refund of host amount</li>
                    <li>• Within 24 hrs of booking start: No refund</li>
                    <li>• Service fees are non-refundable</li>
                  </ul>
                  <p className="mt-2 text-yellow-200/60">Approved refunds are processed within 7–14 business days.</p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => {
                      setShowCancelModal(null);
                      setCancellationPreview(null);
                      setCancelReason('');
                    }}
                    variant="outline"
                    className="flex-1 border-white/10 text-zinc-400 hover:text-white"
                  >
                    Keep Booking
                  </Button>
                  <Button
                    onClick={handleCancelBooking}
                    disabled={cancelling}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dispute Modal */}
        {showDisputeModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Flag size={24} className="text-orange-400" weight="duotone" />
                  Raise a Dispute
                </h3>
                <p className="text-zinc-500 text-sm mt-1">{showDisputeModal.listing_title}</p>
              </div>
              
              <div className="p-6 space-y-4">
                {disputeEligibility[showDisputeModal.id]?.can_dispute === false ? (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 rounded text-red-400">
                    <p className="font-semibold mb-1">Cannot Raise Dispute</p>
                    <p className="text-sm">{disputeEligibility[showDisputeModal.id]?.reason}</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded text-sm">
                      <p className="text-orange-200 font-semibold mb-2">Important Information:</p>
                      <ul className="text-orange-200/80 space-y-1">
                        <li>• Disputes must be raised within 7 days of your booking date</li>
                        <li>• Our team will review your dispute within 48 hours</li>
                        <li>• The host's payout will be held until the dispute is resolved</li>
                        {disputeEligibility[showDisputeModal.id]?.days_remaining && (
                          <li className="text-yellow-400">
                            • You have {disputeEligibility[showDisputeModal.id].days_remaining} day(s) remaining to file a dispute
                          </li>
                        )}
                      </ul>
                    </div>

                    <div>
                      <label className="text-zinc-400 mb-2 block text-sm">Reason for Dispute *</label>
                      <select
                        value={disputeData.reason}
                        onChange={(e) => setDisputeData({ ...disputeData, reason: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-white/10 text-white p-3 rounded"
                      >
                        <option value="">Select a reason...</option>
                        <option value="space_not_as_described">Space was not as described</option>
                        <option value="host_no_show">Host did not show up</option>
                        <option value="safety_concerns">Safety concerns</option>
                        <option value="cleanliness_issues">Cleanliness issues</option>
                        <option value="amenities_missing">Amenities were missing</option>
                        <option value="access_denied">Was denied access to the space</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-zinc-400 mb-2 block text-sm">Describe the issue *</label>
                      <Textarea
                        value={disputeData.description}
                        onChange={(e) => setDisputeData({ ...disputeData, description: e.target.value })}
                        placeholder="Please provide details about what happened..."
                        className="bg-[#0a0a0a] border-white/10 text-white"
                        rows={4}
                      />
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={() => {
                          setShowDisputeModal(null);
                          setDisputeData({ reason: '', description: '' });
                        }}
                        variant="outline"
                        className="flex-1 border-white/10 text-zinc-400 hover:text-white"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSubmitDispute}
                        disabled={submittingDispute || !disputeData.reason || !disputeData.description}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        {submittingDispute ? 'Submitting...' : 'Submit Dispute'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}